import { WhatsappLogo, MapPin, Phone, Globe, Heart, UsersThree, ShieldCheck, Clock, ArrowRight, NotePencil } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useSite, mediaUrl, whatsappHref } from "@/context/SiteContext";
import { useHiringCounty } from "@/lib/useHiringCounty";
import { IMAGES } from "@/lib/data";

const FEATURES = [
  { icon: Heart, label: "Fulfillment That Matters" },
  { icon: UsersThree, label: "Make a Real Impact" },
  { icon: ShieldCheck, label: "Training Provided" },
  { icon: Clock, label: "Full-Time & Part-Time" },
];

export function RecruitmentBanner({ county: explicitCounty }) {
  const content = useSite();
  const { county, candidate } = useHiringCounty(explicitCounty);

  if (content.recruiting_enabled === false) return null;


  const ft = content.recruiting_fulltime !== false;
  const pt = content.recruiting_parttime !== false;
  const empTypes = ft && pt ? "Full-Time & Part-Time" : ft ? "Full-Time" : pt ? "Part-Time" : "";
  const headline = content.recruiting_headline || "Drivers Wanted";
  const subHeadline = county ? `${county} County` : "Across Our Pennsylvania Service Area";
  const desc = content.recruiting_description || "";
  const pay = (content.recruiting_pay || "").trim();
  const btnText = content.recruiting_button_text || "Apply Today";
  const img = content.recruiting_image_id ? mediaUrl(content.recruiting_image_id) : IMAGES.recruitment;
  const waOn = content.whatsapp_enabled !== false && (content.whatsapp_number || "").trim();
  const applyTo = candidate ? `/careers?county=${encodeURIComponent(candidate)}` : "/careers";
  const waUser = content.whatsapp_username || "@caredeserved";
  const phone = content.whatsapp_number || content.phone;

  return (
    <section data-testid="recruitment-banner" className="bg-brand-bg py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-brand-blue shadow-2xl ring-1 ring-white/10">
          {/* Background photo */}
          <img src={img} alt="A CareDeserved transportation driver beside a company van"
            className="absolute inset-0 h-full w-full object-cover object-right" loading="lazy" />
          {/* Scrims — keep text readable on every screen size */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/95 to-brand-blue/30 sm:via-brand-blue/80 sm:to-transparent" />
          <div className="absolute inset-0 bg-brand-blue/40 sm:bg-transparent" />

          <div className="relative z-10 max-w-2xl px-6 py-8 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
            <Logo variant="light" className="h-10 sm:h-12" />

            <h2 data-testid="recruitment-headline" className="mt-6 font-heading text-3xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
              {headline}
              <span className="mt-0.5 block text-brand-teal2">{subHeadline}</span>
            </h2>

            {county && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-purple/90 px-4 py-2 text-sm font-semibold text-white sm:text-base">
                <MapPin size={18} weight="fill" className="text-brand-teal2" /> Live in {county} County? Apply Within.
              </p>
            )}

            {empTypes && (
              <p className="mt-5 font-heading text-lg font-bold text-white sm:text-xl">{empTypes} Positions Available</p>
            )}
            {desc && <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-200 sm:text-base">{desc}</p>}
            {pay && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-semibold text-white">
                <ShieldCheck size={16} weight="fill" className="text-brand-teal2" /> {pay}
              </p>
            )}

            {/* Feature chips */}
            <div className="mt-7 grid max-w-lg grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4">
              {FEATURES.map((f) => (
                <div key={f.label} className="flex flex-col items-center text-center sm:items-start sm:text-left">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-brand-teal2 ring-1 ring-white/20">
                    <f.icon size={22} weight="duotone" />
                  </span>
                  <span className="mt-2 text-xs font-semibold leading-tight text-slate-200">{f.label}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to={applyTo} data-testid="recruitment-apply"
                className="inline-flex h-13 items-center justify-center gap-2 rounded-full bg-brand-purple px-7 py-3 text-base font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-brand-purple2">
                <NotePencil size={20} weight="fill" /> {btnText} <ArrowRight size={18} weight="bold" />
              </Link>
              {waOn && (
                <a href={whatsappHref(content)} target="_blank" rel="noopener noreferrer" data-testid="recruitment-whatsapp"
                  className="inline-flex h-13 items-center justify-center gap-2 rounded-full border border-white/40 bg-white/5 px-6 py-3 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/15">
                  <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" /> Questions? WhatsApp Us
                </a>
              )}
            </div>

            {/* Contact bar */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/15 pt-5 text-sm text-slate-200">
              <a href={`tel:+1${(phone || "").replace(/[^0-9]/g, "")}`} className="inline-flex items-center gap-2 hover:text-white">
                <Phone size={16} weight="fill" className="text-brand-teal2" /> {content.phone}
              </a>
              {waOn && (
                <a href={whatsappHref(content)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white">
                  <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" /> {waUser}
                </a>
              )}
              <span className="inline-flex items-center gap-2">
                <Globe size={16} weight="fill" className="text-brand-teal2" /> {(content.website || "CareDeserved.com").replace(/\/$/, "")}/careers
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
